// Thin native-guarded wrapper around @capacitor/haptics.
//
// Rules:
//  - Web is a no-op (Haptics.* throws / no-ops in browser anyway).
//  - Respects prefers-reduced-motion: if the user has reduced motion on,
//    we skip haptics entirely (matches Apple HIG).
//  - Every call is try/catch'd so a failing haptic never breaks UX.
//  - Use sparingly: tracking start/stop, upload batch success, library refresh,
//    Live Activity "Track now", IAP tip success. No haptic spam.

import { Capacitor } from "@capacitor/core";
import { Haptics, ImpactStyle, NotificationType } from "@capacitor/haptics";

function shouldFire(): boolean {
  if (!Capacitor.isNativePlatform()) return false;
  try {
    if (typeof window !== "undefined" && window.matchMedia) {
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        return false;
      }
    }
  } catch {
    /* ignore */
  }
  return true;
}

export async function hapticImpact(
  style: "light" | "medium" | "heavy" = "medium",
): Promise<void> {
  if (!shouldFire()) return;
  try {
    const map = {
      light: ImpactStyle.Light,
      medium: ImpactStyle.Medium,
      heavy: ImpactStyle.Heavy,
    } as const;
    await Haptics.impact({ style: map[style] });
  } catch {
    /* ignore */
  }
}

export async function hapticSuccess(): Promise<void> {
  if (!shouldFire()) return;
  try {
    await Haptics.notification({ type: NotificationType.Success });
  } catch {
    /* ignore */
  }
}

export async function hapticWarning(): Promise<void> {
  if (!shouldFire()) return;
  try {
    await Haptics.notification({ type: NotificationType.Warning });
  } catch {
    /* ignore */
  }
}

export async function hapticError(): Promise<void> {
  if (!shouldFire()) return;
  try {
    await Haptics.notification({ type: NotificationType.Error });
  } catch {
    /* ignore */
  }
}

export async function hapticSelection(): Promise<void> {
  if (!shouldFire()) return;
  try {
    await Haptics.selectionStart();
    await Haptics.selectionChanged();
    await Haptics.selectionEnd();
  } catch {
    /* ignore */
  }
}
